import React, { useEffect, useState, useRef } from 'react';
// components/pages/BetaLanding.tsx
import { CreditCardIcon, ShieldCheck, Sparkles, Users, BellRing, CheckCircle2, Rocket, Mail, Phone, UserIcon } from 'lucide-react';
const isDev = typeof import.meta !== 'undefined' && (import.meta as any)?.env?.DEV || typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';
const isEmailSyntaxValid = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email.trim());
const COUNT_KEY = 'creditual_waitlist_count';
const COUNT_BASE = 47; // Empezamos con un número que da credibilidad
export default function BetaLanding() {
  const sectionRef = useRef<HTMLElement | null>(null);
  // Form state - simplificado solo a contacto
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  // Validación
  const [touched, setTouched] = useState(false);
  const errors = {
    nombre: nombre.trim().length < 2 ? 'Ingresa tu nombre' : '',
    email: email.trim().length === 0 ? 'Ingresa tu correo' : !isEmailSyntaxValid(email) ? 'Correo inválido' : ''
  };
  const [sent, setSent] = useState(false);
  // Contador de inscritos con animación
  const [count, setCount] = useState<number>(COUNT_BASE);
  const [ping, setPing] = useState(false);
  useEffect(() => {
    const stored = Number(localStorage.getItem(COUNT_KEY));
    const initial = Number.isFinite(stored) && stored >= COUNT_BASE ? stored : COUNT_BASE;
    setCount(initial);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setPing(v => !v), 3000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const onSubmit = () => {
      setCount(c => {
        const next = c + 1;
        localStorage.setItem(COUNT_KEY, String(next));
        return next;
      });
      setPing(true);
      setTimeout(() => setPing(false), 800);
    };
    window.addEventListener('waitlist-submit', onSubmit as EventListener);
    return () => window.removeEventListener('waitlist-submit', onSubmit as EventListener);
  }, []);
  useEffect(() => {
    if (window.location.hash === '#onboarding' && sectionRef.current) {
      setTimeout(() => sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      }), 0);
    }
  }, []);
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    const hasSyncErrors = Object.values(errors).some(Boolean);
    if (hasSyncErrors) return;
    const payload = {
      nombre: nombre.trim(),
      email: email.trim(),
      telefono: telefono.trim(),
      utm: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
      path: window.location.pathname + window.location.hash,
      ts: new Date().toISOString(),
      type: 'waitlist'
    };
    if (isDev) console.log('WAITLIST_SUBMIT', payload);
    window.dispatchEvent(new CustomEvent('waitlist-submit', {
      detail: payload
    }));
    setSent(true);
  }
  return <main className="bg-white" data-id="element-70">
      <section id="onboarding" ref={sectionRef} className="relative overflow-hidden bg-gradient-to-b from-[#E8EDF5] to-white py-12 md:py-20" data-id="element-71">
        {/* Decor */}
        <div className="pointer-events-none absolute -top-24 right-[-120px] h-64 w-64 rounded-full bg-[#2B5FD9]/10 blur-3xl" data-id="element-72" />
        <div className="pointer-events-none absolute -bottom-24 left-[-120px] h-64 w-64 rounded-full bg-[#1E2A3A]/10 blur-3xl" data-id="element-73" />

        <div className="container mx-auto max-w-5xl px-4" data-id="element-74">
          {/* Badge de estado */}
          <div className="mb-4 flex flex-wrap items-center justify-center gap-3" data-id="element-75">
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-2 text-sm font-semibold text-amber-800" data-id="element-76">
              <Rocket className="h-4 w-4" data-id="element-77" />
              Próximamente
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#2B5FD9]/5 px-4 py-2 text-sm font-medium text-[#2B5FD9]" data-id="element-78">
              <Sparkles className="h-4 w-4" data-id="element-79" />
              Afinando últimos detalles
            </span>
          </div>

          {/* Headline + contador */}
          <div className="text-center mb-8 md:mb-12" data-id="element-80">
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#0A1628] mb-4 leading-tight" data-id="element-81">
              El futuro de los créditos
              <br data-id="element-82" />
              <span className="text-[#2B5FD9]" data-id="element-83">está por llegar</span>
            </h1>
            <p className="text-lg md:text-xl text-[#6B7280] max-w-2xl mx-auto mb-6" data-id="element-84">
              Estamos creando la forma más simple y transparente de conseguir tu
              crédito. Un solo formulario. Todas las instituciones financieras.
              Las mejores tasas.
            </p>
            {/* Contador prominente */}
            <div className="inline-flex items-center gap-3 bg-white rounded-2xl shadow-lg px-6 py-4 border border-[#2B5FD9]/20" data-id="element-85">
              <Users className="h-6 w-6 text-[#2B5FD9]" data-id="element-86" />
              <div className="text-left" data-id="element-87">
                <div className="text-3xl font-bold text-[#0A1628]" data-id="element-88">
                  {count.toLocaleString('es-CL')}
                </div>
                <div className="text-sm text-[#6B7280]" data-id="element-89">
                  personas en lista de espera
                </div>
              </div>
              <span className={`ml-2 inline-block h-3 w-3 rounded-full transition-all duration-300 ${ping ? 'bg-[#2B5FD9] scale-110' : 'bg-gray-300'}`} aria-hidden="true" data-id="element-90" />
            </div>
          </div>

          {/* Formulario o confirmación */}
          {sent ? <div className="mx-auto max-w-2xl" data-id="element-91">
              <div className="rounded-2xl border-2 border-[#2B5FD9] bg-white p-8 shadow-xl text-center" data-id="element-92">
                <div className="mb-4 flex justify-center" data-id="element-93">
                  <div className="rounded-full bg-[#2B5FD9]/10 p-4" data-id="element-94">
                    <CheckCircle2 className="h-12 w-12 text-[#2B5FD9]" data-id="element-95" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3" data-id="element-96">
                  ¡Bienvenido a la lista de espera!
                </h3>
                <p className="text-[#6B7280] mb-6" data-id="element-97">
                  <strong data-id="element-98">{nombre}</strong>, serás uno de los primeros en
                  conocer Creditual cuando estemos listos. Te contactaremos a{' '}
                  <strong data-id="element-99">{email}</strong> muy pronto.
                </p>
                <div className="bg-[#E8EDF5] rounded-xl p-4 mb-6" data-id="element-100">
                  <p className="text-sm text-[#6B7280] mb-2" data-id="element-101">
                    Mientras tanto, te mantendremos informado sobre:
                  </p>
                  <ul className="text-sm text-[#0A1628] space-y-1" data-id="element-102">
                    <li data-id="element-103">✓ Fecha de lanzamiento</li>
                    <li data-id="element-104">✓ Acceso anticipado exclusivo</li>
                    <li data-id="element-105">✓ Beneficios para early adopters</li>
                  </ul>
                </div>
                <button className="text-sm text-[#2B5FD9] underline hover:text-[#1E4BB8]" onClick={() => setSent(false)} data-id="element-106">
                  Editar mis datos
                </button>
              </div>
            </div> : <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-8 items-start" data-id="element-107">
              {/* Formulario simplificado */}
              <form onSubmit={onSubmit} className="rounded-2xl border border-[#2B5FD9]/20 bg-white p-6 md:p-8 shadow-lg" data-id="element-108">
                <div className="mb-6 flex items-center gap-2 text-[#0A1628]" data-id="element-109">
                  <BellRing className="h-5 w-5 text-[#2B5FD9]" data-id="element-110" />
                  <h3 className="text-xl font-bold" data-id="element-111">
                    Únete a la lista de espera
                  </h3>
                </div>
                <p className="text-sm text-[#6B7280] mb-6" data-id="element-112">
                  Déjanos tus datos y te avisaremos cuando estemos listos para
                  ayudarte a conseguir el mejor crédito.
                </p>
                <div className="space-y-4" data-id="element-113">
                  {/* Nombre */}
                  <div data-id="element-114">
                    <label className="mb-2 block text-sm font-medium text-[#0A1628]" data-id="element-115">
                      Nombre completo *
                    </label>
                    <div className="relative" data-id="element-116">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-117" />
                      <input required type="text" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="Tu nombre y apellido" value={nombre} onChange={e => setNombre(e.target.value)} onBlur={() => setTouched(true)} data-id="element-118" />
                    </div>
                    {touched && errors.nombre && <p className="mt-1 text-xs text-red-600" data-id="element-119">
                        {errors.nombre}
                      </p>}
                  </div>
                  {/* Email */}
                  <div data-id="element-120">
                    <label className="mb-2 block text-sm font-medium text-[#0A1628]" data-id="element-121">
                      Correo electrónico *
                    </label>
                    <div className="relative" data-id="element-122">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-123" />
                      <input required type="email" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="tu@correo.cl" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setTouched(true)} data-id="element-124" />
                    </div>
                    {touched && errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-125">
                        {errors.email}
                      </p>}
                  </div>
                  {/* Teléfono (opcional) */}
                  <div data-id="element-126">
                    <label className="mb-2 block text-sm font-medium text-[#0A1628]" data-id="element-127">
                      Teléfono (opcional)
                    </label>
                    <div className="relative" data-id="element-128">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-129" />
                      <input type="tel" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#2B5FD9] focus:ring-2 focus:ring-[#2B5FD9]/20" placeholder="+56 9 1234 5678" value={telefono} onChange={e => setTelefono(e.target.value)} data-id="element-130" />
                    </div>
                    <p className="mt-1 text-xs text-[#6B7280]" data-id="element-131">
                      Para enviarte actualizaciones por WhatsApp
                    </p>
                  </div>
                </div>
                <button type="submit" className="mt-6 w-full rounded-xl bg-[#2B5FD9] px-6 py-4 font-bold text-white hover:bg-[#1E4BB8] transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all" onClick={() => setTouched(true)} data-id="element-132">
                  Empieza ahora
                </button>
                <p className="mt-4 text-xs text-center text-[#6B7280]" data-id="element-133">
                  Al unirte, aceptas recibir notificaciones sobre el lanzamiento
                  de Creditual. Puedes darte de baja en cualquier momento.
                </p>
              </form>

              {/* Beneficios de early access */}
              <aside className="rounded-2xl border border-[#2B5FD9]/20 bg-white p-6 shadow-lg" data-id="element-134">
                <h3 className="text-lg font-bold text-[#0A1628] mb-4" data-id="element-135">
                  ¿Qué recibirás?
                </h3>
                <ul className="space-y-4 text-sm text-[#6B7280]" data-id="element-136">
                  <li className="flex gap-3" data-id="element-137">
                    <CheckCircle2 className="h-5 w-5 text-[#2B5FD9] flex-shrink-0 mt-0.5" data-id="element-138" />
                    <div data-id="element-139">
                      <strong className="text-[#0A1628]" data-id="element-140">
                        Acceso anticipado
                      </strong>
                      <p className="text-xs mt-1" data-id="element-141">
                        Serás de los primeros en usar la plataforma
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-142">
                    <CheckCircle2 className="h-5 w-5 text-[#2B5FD9] flex-shrink-0 mt-0.5" data-id="element-143" />
                    <div data-id="element-144">
                      <strong className="text-[#0A1628]" data-id="element-145">
                        Beneficios exclusivos
                      </strong>
                      <p className="text-xs mt-1" data-id="element-146">
                        Condiciones especiales para early adopters
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-147">
                    <CheckCircle2 className="h-5 w-5 text-[#2B5FD9] flex-shrink-0 mt-0.5" data-id="element-148" />
                    <div data-id="element-149">
                      <strong className="text-[#0A1628]" data-id="element-150">
                        Asesoría prioritaria
                      </strong>
                      <p className="text-xs mt-1" data-id="element-151">
                        Atención personalizada desde el día uno
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-152">
                    <CheckCircle2 className="h-5 w-5 text-[#2B5FD9] flex-shrink-0 mt-0.5" data-id="element-153" />
                    <div data-id="element-154">
                      <strong className="text-[#0A1628]" data-id="element-155">
                        Sin costos ocultos
                      </strong>
                      <p className="text-xs mt-1" data-id="element-156">
                        Transparencia total, siempre
                      </p>
                    </div>
                  </li>
                </ul>
                <div className="mt-6 rounded-xl bg-[#E8EDF5] p-4" data-id="element-157">
                  <p className="text-xs text-[#6B7280] mb-2" data-id="element-158">
                    <strong className="text-[#0A1628]" data-id="element-159">
                      ¿Por qué lista de espera?
                    </strong>
                  </p>
                  <p className="text-xs text-[#6B7280]" data-id="element-160">
                    Estamos afinando cada detalle para ofrecerte la mejor
                    experiencia. Queremos lanzar cuando todo esté perfecto.
                  </p>
                </div>
              </aside>
            </div>}

          {/* Social proof adicional */}
          <div className="mt-12 text-center" data-id="element-161">
            <p className="text-sm text-[#6B7280] mb-4" data-id="element-162">
              Únete a las {count} personas que ya están esperando
            </p>
            <div className="flex justify-center gap-2" data-id="element-163">
              {[...Array(Math.min(10, Math.floor(count / 5)))].map((_, i) => <div key={i} className="w-8 h-8 rounded-full bg-[#2B5FD9]/10 border-2 border-white" style={{
              marginLeft: i > 0 ? '-8px' : '0'
            }} data-id="element-164" />)}
              {count > 50 && <div className="w-8 h-8 rounded-full bg-[#2B5FD9] border-2 border-white flex items-center justify-center text-xs font-bold text-white" style={{
              marginLeft: '-8px'
            }} data-id="element-165">
                  +
                </div>}
            </div>
          </div>
        </div>
      </section>
    </main>;
}